import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { HubPerformance } from '@/hooks/useGestorPerformance';
import { TrendingUp, TrendingDown, Minus, Award } from 'lucide-react';

interface HubRankingTableProps {
  data: HubPerformance[];
}

const HubRankingTable: React.FC<HubRankingTableProps> = ({ data }) => {
  // Calcular score composto para ranking
  const rankedData = data.map((hub) => {
    // Score = 40% taxa conclusão + 30% utilização + 30% (100 - % atrasos)
    const taxaAtrasos = hub.inspecoes_agendadas > 0 
      ? (hub.inspecoes_atrasadas / hub.inspecoes_agendadas) * 100 
      : 0;
    
    const score = Math.round(
      (hub.taxa_conclusao * 0.4) + 
      (Math.min(hub.utilizacao_capacidade, 100) * 0.3) + 
      ((100 - taxaAtrasos) * 0.3)
    );

    return {
      ...hub,
      score,
      taxaAtrasos: Math.round(taxaAtrasos)
    };
  }).sort((a, b) => b.score - a.score);

  const getScoreBadge = (score: number, index: number) => {
    if (index === 0) {
      return (
        <Badge className="bg-yellow-500/20 text-yellow-600 border-yellow-500/30 gap-1">
          <Award className="w-3 h-3" />
          {score}
        </Badge>
      );
    }
    if (score >= 70) {
      return <Badge variant="outline" className="bg-success/10 text-success border-success/30">{score}</Badge>;
    }
    if (score >= 50) {
      return <Badge variant="outline" className="bg-warning/10 text-warning border-warning/30">{score}</Badge>;
    }
    return <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive/30">{score}</Badge>;
  };

  const getTrendIcon = (taxa: number) => {
    if (taxa >= 70) return <TrendingUp className="w-4 h-4 text-success" />;
    if (taxa >= 40) return <Minus className="w-4 h-4 text-muted-foreground" />;
    return <TrendingDown className="w-4 h-4 text-destructive" />;
  };

  return (
    <Card>
      <CardHeader className="py-3">
        <CardTitle className="text-sm font-medium">Ranking de Hubs</CardTitle>
      </CardHeader>
      <CardContent>
        {rankedData.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground text-sm">
            Nenhum hub encontrado
          </div>
        ) : (
          <div className="space-y-2">
            {rankedData.map((hub, index) => (
              <div
                key={hub.hub_id}
                className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                  index === 0 ? 'bg-yellow-500/5 border-yellow-500/20' : 'hover:bg-muted/30'
                }`}
              >
                <div className="w-6 text-center">
                  <span className={`text-sm font-semibold ${index === 0 ? 'text-yellow-600' : 'text-muted-foreground'}`}>
                    #{index + 1}
                  </span>
                </div>
                
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{hub.hub_name}</p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-xs text-muted-foreground">
                      {hub.total_profissionais} prof.
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {hub.inspecoes_concluidas}/{hub.inspecoes_agendadas} insp.
                    </span>
                    {hub.inspecoes_atrasadas > 0 && (
                      <span className="text-xs text-destructive">
                        {hub.inspecoes_atrasadas} atrasadas
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {getTrendIcon(hub.taxa_conclusao)}
                  {getScoreBadge(hub.score, index)}
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="text-xs text-muted-foreground mt-3 text-center">
          Score = 40% conclusão + 30% utilização + 30% pontualidade
        </p>
      </CardContent>
    </Card>
  );
};

export default HubRankingTable;
